import { FetchUtil } from "../util/FetchUtil";
import { HealioErrorModal } from "../components/healioGlobal/HealioErrorModal";
import { IQuestionAnswerServiceEndpoint } from "../models/QuestionGroup/IQuestionAnswerServiceEndpoint";
import { StringUtil } from "../util/StringUtil";
import { WyanokeUtil } from "../util/WyanokeUtil";
import { IQuestionGroupModel } from "../models/QuestionGroup/IQuestionGroupModel";
import { IQuestionAnswerSaveResponse } from "../models/QuestionGroup/IQuestionAnswerSaveResponse";

interface IPeerComparisonResponse {
  answerText: string;
  count: number;
  answerid: string;
}

export class QuestionAnswerService {
  constructor(private readonly _endpoint: IQuestionAnswerServiceEndpoint) { }

  public async getAnswers(
    questionGroupId: string
  ): Promise<IQuestionGroupModel | null> {
    const endpoint = this._endpoint.load;
    if (!endpoint) {
      throw new Error("Could not resolve endpoint.");
    }
    const profile = await WyanokeUtil.loadUserProfile(true, false);
    if (!profile) {
      return null;
    }

    const contactId = profile.contactid || "";
    const values = {
      contactId,
      encodedContactId: encodeURIComponent(contactId),
      encodedQuestionGroupId: encodeURIComponent(questionGroupId),
      questionGroupId
    };

    let questionGroup: IQuestionGroupModel | null = null;

    const url = StringUtil.supplant(endpoint.url, values) || "";

    let raw;
    try{
      raw = await FetchUtil.sendMessage({
        method: endpoint.method,
        url
      });
    } catch{
      raw = undefined;
    }

    if (typeof raw !== "object") {
      const msg = "Unexpected response from server";
      console.error(msg, raw);
      HealioErrorModal.triggerHealioErrorModalEvent();

      return null;
    }

    questionGroup = raw as IQuestionGroupModel;

    return questionGroup;
  }

  public async getCompareToPeers(questionGroupId: string): Promise<Record<string, number> | null> {
    const endpoint = this._endpoint.compareToPeers;
    if (!endpoint) {
      console.warn("Compare to peers endpoint not configured");
      return null;
    }

    const values = {
      questionGroupId
    };

    const url = StringUtil.supplant(endpoint.url, values);
    if (!url) {
      console.warn("Invalid endpoint URL");
      return null;
    }

    try {
      const result = await FetchUtil.sendMessage({
        method: endpoint.method,
        url
      });

      if (!Array.isArray(result)) {
        console.warn("Invalid response from compare to peers endpoint", result);
        return null;
      }

      const responses = result as IPeerComparisonResponse[];
      const totalResponses = responses.reduce((sum, r) => sum + r.count, 0);

      // Convert counts to percentages
      return responses.reduce((acc, response) => {
        acc[response.answerid] = totalResponses > 0
          ? (response.count / totalResponses) * 100
          : 0;
        return acc;
      }, {} as Record<string, number>);

    } catch (error) {
      console.error("Error fetching peer comparison data:", error);
      return null;
    }
  }

  public async saveAnswers(
    questiongroup: IQuestionGroupModel
  ): Promise<boolean> {

    const endpoint = this._endpoint.save;
    if (!endpoint) {
      throw new Error("Could not resolve endpoint.");
    }

    const result = (await FetchUtil.sendMessage(
      endpoint,
      questiongroup
    )) as IQuestionAnswerSaveResponse;

    const expected = endpoint.method.toLowerCase() + " ok";

    if (!result.response?.message || result.response.message.toLowerCase() !== expected) {
      console.error("Unexpected response from server.");

      return false;
    }

    return true;
  }

}
